import { useMemo } from 'react';
import { FilterableMultiSelect, Link, Button } from '@carbon/react';
import {
  SplitScreen,
  Row,
  FlowData,
  LocationPerson,
  Document,
} from '@carbon/react/icons';
import style from './v2-sidebar.module.scss';
import { InterestTypeLatticeFilter } from './interest-type-filter';
import { FloatingTooltip } from 'components/floating-tooltip';

type LayoutMode = 'side-by-side' | 'stacked';
type GraphDirection = 'vertical' | 'horizontal';

export interface TitleChainValidatorFilters {
  selectedLocations: string[];
  interestTypeFilter: string[];
  edgeTypeFilter: string[];
}

export type ConsolidationLevel = 'operation' | 'transaction' | 'document';

export interface LayoutSettings {
  layoutMode: LayoutMode;
  showGraph: boolean;
  showTable: boolean;
  showCurrentOwnership: boolean;
  showRunsheet: boolean;
  showMap: boolean;
  graphDirection: GraphDirection;
  consolidation: ConsolidationLevel;
}

const EDGE_TYPE_ITEMS = [
  { id: 'E1', text: 'E1: Estate Severance' },
  { id: 'E2', text: 'E2: Property Severance' },
  { id: 'E3', text: 'E3: Encumbrance Severance' },
  { id: 'E4', text: 'E4: Interest Division' },
  { id: 'E5', text: 'E5: Consolidation' },
  { id: 'E6', text: 'E6: Encumbrance Pointer Update' },
  { id: 'E7', text: 'E7: Lease Termination' },
];

// =============================================================================
// Extracted Sub-Components (used by header menus and sidebar)
// =============================================================================

interface PanelVisibilityControlsProps {
  layoutSettings: LayoutSettings;
  onLayoutChange: (settings: Partial<LayoutSettings>) => void;
  tooltipPlacement?: 'top' | 'bottom';
}

export const PanelVisibilityControls = ({
  layoutSettings,
  onLayoutChange,
  tooltipPlacement = 'top',
}: PanelVisibilityControlsProps) => (
  <div className={style.iconToggleGroup}>
    <FloatingTooltip content="Graph" placement={tooltipPlacement} asChild>
      <Button
        hasIconOnly
        renderIcon={FlowData}
        iconDescription="Graph"
        size="sm"
        kind={layoutSettings.showGraph ? 'primary' : 'ghost'}
        disabled={
          layoutSettings.showGraph &&
          !layoutSettings.showCurrentOwnership &&
          !layoutSettings.showRunsheet
        }
        onClick={() => onLayoutChange({ showGraph: !layoutSettings.showGraph })}
      />
    </FloatingTooltip>
    <FloatingTooltip content="Ownership" placement={tooltipPlacement} asChild>
      <Button
        hasIconOnly
        renderIcon={LocationPerson}
        iconDescription="Ownership"
        size="sm"
        kind={
          layoutSettings.showCurrentOwnership || layoutSettings.showMap
            ? 'primary'
            : 'ghost'
        }
        disabled={
          (layoutSettings.showCurrentOwnership || layoutSettings.showMap) &&
          !layoutSettings.showGraph &&
          !layoutSettings.showRunsheet
        }
        onClick={() => {
          const isOn =
            layoutSettings.showCurrentOwnership || layoutSettings.showMap;
          if (isOn) {
            onLayoutChange({
              showCurrentOwnership: false,
              showMap: false,
              showTable: layoutSettings.showRunsheet,
            });
          } else {
            onLayoutChange({
              showCurrentOwnership: true,
              showTable: true,
            });
          }
        }}
      />
    </FloatingTooltip>
    <FloatingTooltip content="Runsheet" placement={tooltipPlacement} asChild>
      <Button
        hasIconOnly
        renderIcon={Document}
        iconDescription="Runsheet"
        size="sm"
        kind={layoutSettings.showRunsheet ? 'primary' : 'ghost'}
        disabled={
          layoutSettings.showRunsheet &&
          !layoutSettings.showGraph &&
          !layoutSettings.showCurrentOwnership
        }
        onClick={() => {
          const next = !layoutSettings.showRunsheet;
          onLayoutChange({
            showRunsheet: next,
            showTable: next || layoutSettings.showCurrentOwnership,
          });
        }}
      />
    </FloatingTooltip>
  </div>
);

interface PanelLayoutControlsProps {
  layoutSettings: LayoutSettings;
  onLayoutChange: (settings: Partial<LayoutSettings>) => void;
  tooltipPlacement?: 'top' | 'bottom';
}

export const PanelLayoutControls = ({
  layoutSettings,
  onLayoutChange,
  tooltipPlacement = 'top',
}: PanelLayoutControlsProps) => (
  <div className={style.iconToggleGroup}>
    <FloatingTooltip
      content="Side-by-side"
      placement={tooltipPlacement}
      asChild
    >
      <Button
        hasIconOnly
        renderIcon={SplitScreen}
        iconDescription="Side-by-side"
        kind={
          layoutSettings.layoutMode === 'side-by-side' ? 'primary' : 'ghost'
        }
        size="sm"
        onClick={() => onLayoutChange({ layoutMode: 'side-by-side' })}
      />
    </FloatingTooltip>
    <FloatingTooltip content="Stacked" placement={tooltipPlacement} asChild>
      <Button
        hasIconOnly
        renderIcon={Row}
        iconDescription="Stacked"
        kind={layoutSettings.layoutMode === 'stacked' ? 'primary' : 'ghost'}
        size="sm"
        onClick={() => onLayoutChange({ layoutMode: 'stacked' })}
      />
    </FloatingTooltip>
  </div>
);

interface DocumentIndexFilterControlsProps {
  filters: TitleChainValidatorFilters;
  uniqueLocations: string[];
  onFiltersChange: (filters: Partial<TitleChainValidatorFilters>) => void;
  onResetFilters: () => void;
}

export const DocumentIndexFilterControls = ({
  filters,
  uniqueLocations,
  onFiltersChange,
  onResetFilters,
}: DocumentIndexFilterControlsProps) => {
  const hasActiveFilters = useMemo(
    () =>
      filters.selectedLocations.length > 0 ||
      filters.interestTypeFilter.length > 0 ||
      filters.edgeTypeFilter.length > 0,
    [filters]
  );

  return (
    <>
      {hasActiveFilters && (
        <Link className={style.resetLink} onClick={onResetFilters}>
          Reset Filters
        </Link>
      )}
      <div className={style.section}>
        <div className={style.subsectionTitle}>Properties</div>
        <div className={style.locationFilter}>
          <FilterableMultiSelect
            id="property-filter"
            titleText=""
            items={uniqueLocations}
            itemToString={(item: any) => item || ''}
            placeholder="Type to search properties"
            initialSelectedItems={filters.selectedLocations}
            onChange={({ selectedItems }: any) =>
              onFiltersChange({ selectedLocations: selectedItems })
            }
          />
        </div>
      </div>
      <div className={style.section}>
        <div className={style.subsectionTitle}>Interest Types</div>
        <InterestTypeLatticeFilter
          selected={filters.interestTypeFilter}
          onChange={(next) => onFiltersChange({ interestTypeFilter: next })}
        />
      </div>
      <div className={style.section}>
        <div className={style.subsectionTitle}>Conveyance Operation Types</div>
        <div className={style.locationFilter}>
          <FilterableMultiSelect
            id="edge-type-filter"
            titleText=""
            items={EDGE_TYPE_ITEMS}
            itemToString={(item: any) => item?.text || ''}
            placeholder="Type to search"
            initialSelectedItems={EDGE_TYPE_ITEMS.filter((item) =>
              filters.edgeTypeFilter.includes(item.id)
            )}
            onChange={({ selectedItems }: any) =>
              onFiltersChange({
                edgeTypeFilter: selectedItems.map((item: any) => item.id),
              })
            }
          />
        </div>
      </div>
    </>
  );
};
